import React, { useEffect, useState } from 'react';
import { Modal, Box, ButtonGroup, Button, IconButton, InputAdornment, List, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import Styles from '../../../common/styles/styles';
import { validateFirstName, validateLastName, validateUserName, validatePhoneNumber, validateEmailAddr, validatePassword, validateConfirmPassword } from '../../../common/utils/validationUtil';
import { useUser } from '../../../../userContext';
import { capFirstLetter, formatPhoneNo } from '../../../common/utils/massageDataUtil';

export default function UserModal({ user, onTriggerCreate, onTriggerUpdate }) {
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState('');
  const { userRole } = useUser();
  
  const isFormValid = Object.values(errors).filter(value => value !=='').length > 0;

  const [formData, setFormData] = useState({
      id: '',
      firstname: '',
      lastname: '',
      emailaddr: '',
      username: '',
      phone:  '',
      groupname: '',
      cli: false,
      disabled: false,
      ccg_employee: false,
      password: '',
      confirmpassword: '',
  });

  const handleOpen = () => {
    setOpen(true);
    setTitle('Create User');    
    setFormData({ ...formData, id: '', firstname: '', lastname: '', emailaddr: '', username: '', phone: '', groupname: '', cli: false, disabled: false, ccg_employee: false, password: '', confirmpassword: ''});
  }

  const handleEditOpen = () => {
    setOpen(true);
    setTitle('Update User');
  }

  const handleUpdate = async (event) => {
      const result = await createUser(event);      
      
      if (result === 'success') {
        setOpen(false);
        if ( title === 'Create User' ) {
          onTriggerCreate(formData);
        } else {
          onTriggerUpdate(formData);
        }      
      }
  }

  const handleClose = async () => {
    setOpen(false);
    if (formData.id === '') {    
      await setFormData({ ...formData, id: '', firstname: '', lastname: '', emailaddr: '', username: '', phone: '', groupname: '', cli: false, disabled: false, ccg_employee: false, password: '', confirmpassword: ''});
    }
  }
  
  const togglePasswordVisibility = (field) => {
      setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
      }));
  };
  
  const [showPasswords, setShowPasswords] = useState({
      current: false,
      new: false,
      confirm: false,
  });

  const handleInputChange = (field) => (e) => {      

      let value = e.target.value;
      if ( field === 'firstname' || field === 'lastname' ) {
        value = capFirstLetter(value);
      } else if ( field === 'phone' ) {
        value = formatPhoneNo(value, e);
      }
      
      if (field === 'groupname') {
        handleGroupChange(value);
      } else if (field === 'disabled') {
        handleDisabledChange(value);
      }
      setFormData({ ...formData, [field]: value });
      
      const newErrors = { ...errors };
      
      if (field === 'firstname') newErrors.firstname = validateFirstName(value);
      if (field === 'lastname') newErrors.lastname = validateLastName(value);
      if (field === 'username') newErrors.username = validateUserName(value);
      if (field === 'phone') newErrors.phone = validatePhoneNumber(value);
      if (field === 'emailaddr') newErrors.emailaddr = validateEmailAddr(value);
      if (field === 'password') newErrors.password = validatePassword(value);
      if (field === 'confirmpassword') newErrors.confirmpassword = validateConfirmPassword(formData.password, value);
      
      setErrors(newErrors);
  };
  
  const massageData = () => {    
    setFormData(prev => ({ ...prev, 'firstname': capFirstLetter(formData.firstname) }));
    setFormData(prev => ({ ...prev, 'lastname': capFirstLetter(formData.lastname) }));
    setFormData(prev => ({ ...prev, 'username': formData.username.trim().toLowerCase() }));
  }

  const groups = [
    { value: 'admin', label: 'Admin', disabled: true },
    { value: 'ccg-user', label: 'User', disabled: true },
    { value: 'Engineer', label: 'Engineer' },
    { value: 'madmin', label: 'Madmin', disabled: true }
  ];

  const options = [
    { value: true, label: 'True' },
    { value: false, label: 'False' }
  ];

  const handleGroupChange = (value) => {
    formData.cli = false;
    formData.groupname = value;
  }

  const handleDisabledChange = (isTrue) => {
    if (isTrue) {
      formData.cli = false;
    }
  } 

  const createUser = async (event) => {
    event.preventDefault();
    massageData();
    let result = null;

    try {
        
        if (formData.id === '') {
          const response = await fetch(`${process.env.REACT_APP_API}/users/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(formData),
          });

            result = await response.json();
            if (result.message === 'success') {
              await handleClose();
            } else {
              toast.error(result.message);
            }
        } else {
            const response = await fetch(`${process.env.REACT_APP_API}/users/update`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            result = await response.json();
            if (result.message === 'success') {
              await handleClose();
            } else {
              toast.error(result.message);
            }
          }
          return result.message;
    } catch (error) {
      toast.error('Network error.  Please try again: ', error);
      return false;
    }
  }
  
  const textFieldStyles = Styles.textFieldStyles;

  useEffect(() => {
    if (user.id !== undefined) {
      setFormData(user);      
    } 
  }, [user]);

  return (
    <>
        { formData.id === "" && 
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleOpen}>
            Add New
          </Button>
        }
        
        { formData.id !== '' && 
          <Button variant="outlined" color="success" size="small" onClick={handleEditOpen}>
              <EditIcon />
          </Button>
        }
        
        <Modal 
          open={open} 
          onClose={handleClose}
          style={{
            overlay: {
              backgroundColor: '#080030', // Semi-transparent black
            },
            content: {
              backgroundColor: '#080030',
            },
          }}
        >
            <Box sx={{color: 'white', p: 4}}>
                <Box
                  component={Paper}
                  elevation={3}
                  sx={{
                    mx: 'auto',
                    width: 400,
                    bgcolor: '#1e1e1e',
                    p: 2,
                  }}
                >
                  <Typography variant="h4" component="h2" color="white" mb={2}>
                    {title}
                  </Typography>

                  <Box component="form" onSubmit={handleUpdate}>
                    <Stack spacing={2}>
                      <TextField
                        required
                        fullWidth
                        label="First Name"
                        value={formData.firstname}
                        onChange={handleInputChange('firstname')}
                        error={!!errors.firstname}
                        // helperText={errors.firstname ? "Please enter a valid name." : ""}
                        helperText= 
                        {
                          errors.firstname ? 
                            errors.firstname.map((err) => (
                              <List>{err}</List>
                            ))
                          :
                            null
                        }
                        sx={ textFieldStyles }
                        slotProps={{
                            input: {
                              autoComplete: 'firstname',
                            }
                        }}
                      />
                      <TextField
                        required
                        fullWidth
                        label="Last Name"
                        value={formData.lastname}
                        onChange={handleInputChange('lastname')}
                        error={!!errors.lastname}
                        // helperText={errors.lastname}
                        helperText= 
                        {
                          errors.lastname ? 
                            errors.lastname.map((err) => (
                              <List>{err}</List>
                            ))
                          :
                            null
                        }
                        sx={ textFieldStyles }
                        slotProps={{
                            input: {
                              autoComplete: 'lastname',
                            }
                        }}
                      />
                      { formData.id === "" && 
                        <TextField
                          required
                          fullWidth
                          label="User Name"
                          value={formData.username}
                          onChange={handleInputChange('username')}
                          error={!!errors.username}
                          helperText={errors.username}
                          sx={ textFieldStyles }                          
                          slotProps={{
                              input: {
                                autoComplete: 'username',
                              }
                          }}
                        />
                      }
                      <TextField
                        required
                        fullWidth
                        type="phone"
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        sx={ textFieldStyles }                        
                        slotProps={{
                            input: {
                              autoComplete: 'phone',
                            }
                        }}
                      />
                      <TextField
                        required
                        fullWidth
                        type="email"
                        label="Email Address"
                        value={formData.emailaddr}
                        onChange={handleInputChange('emailaddr')}
                        error={!!errors.emailaddr}
                        helperText={errors.emailaddr}
                        sx={ textFieldStyles }                        
                        slotProps={{
                            input: {
                              autoComplete: 'emailaddr',
                            }
                        }}
                      />
                      <TextField
                        select 
                        required
                        fullWidth
                        label="Group"
                        value={formData.groupname}
                        onChange={handleInputChange('groupname')}
                        sx={ textFieldStyles }
                      >
                        {groups.map((group) => (
                        
                            userRole === 'admin' ?
                                <MenuItem key={group.value} value={group.value}>
                                    {group.label}
                                </MenuItem>
                            :
                            group.label === 'Engineer' ? 
                                <MenuItem key={group.value} value={group.value}>
                                    {group.label}
                                </MenuItem>
                            : 
                                <MenuItem key={group.value} value={group.value} disabled>
                                    {group.label}
                                </MenuItem>
                          ))}
                    </TextField>
                    { formData.groupname === 'admin' && 
                      <TextField
                          select 
                          required
                          fullWidth
                          label="Cli"
                          value={formData.cli}
                          onChange={handleInputChange('cli')}
                          sx={ textFieldStyles }
                        >
                          {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </TextField>     
                    }
                    <TextField
                        select 
                        required
                        fullWidth
                        label="Disabled"
                        value={formData.disabled}
                        onChange={handleInputChange('disabled')}
                        sx={ textFieldStyles }  
                      >
                        {options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select 
                        required
                        fullWidth
                        label="CCG Employee"
                        value={formData.ccg_employee}
                        onChange={handleInputChange('ccg_employee')}
                        sx={ textFieldStyles }  
                      >
                        {options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </TextField>
                    { formData.id === "" && 
                        <TextField
                            fullWidth
                            label="Password"
                            type={showPasswords.newPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleInputChange('password')}
                            error={!!errors.password}
                            helperText= 
                            {
                              errors.password ? 
                                errors.password.map((err) => (
                                  <List>{err}</List>
                                ))
                              :
                                null
                            }
                            sx={ textFieldStyles }
                            required
                            slotProps={{
                                input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => togglePasswordVisibility('newPassword')}
                                        edge="end"
                                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                    >
                                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                ),
                                }
                            }}
                        />
                      }
                      { formData.id === "" && 
                        <TextField
                          fullWidth
                          label="Confirm Password"
                          type={showPasswords.confirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleInputChange('confirmpassword')}
                          error={!!errors.confirmpassword}
                          helperText=
                          {
                              errors.confirmpassword ? 
                                errors.confirmpassword.map((err) => (
                                  <List>{err}</List>
                                )) 
                                : 
                                null
                          }
                          sx={ textFieldStyles }
                          required
                          slotProps={{
                              input: {
                              endAdornment: (
                                  <InputAdornment position="end">
                                  <IconButton
                                      onClick={() => togglePasswordVisibility('confirmPassword')}
                                      edge="end"
                                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                  >
                                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                  </InputAdornment>
                              ),
                              }
                          }}                    
                        />
                      }
                  
                  <ButtonGroup 
                    size="small"
                    variant="contained" 
                    aria-label="Basic button group" 
                    fullWidth
                    sx={{ mt:2 }}
                  >
                    <Button
                      onClick={handleClose}
                      type="cancel"
                      variant="contained"
                      sx={ Styles.buttonStyle }
                    >
                      Cancel
                    </Button>              
                    <Button
                      type="submit"                      
                      variant="contained"
                      disabled={isFormValid}
                      sx={{ ...Styles.buttonStyle,  ml:4 }}
                    >
                      {title}
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Box>
            </Box>
          </Box>          
        </Modal>
    </>
  );
}